import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "../../../components/Navbar2";
import PricingToggle from "../ai/misc/PricingToggle";
import AddOnsConsole from "./misc/AddOnsConsole";
import SelectedFeaturesFloating, {
  SelectedItem,
} from "../misc/SelectedFeaturesFloating";
import {
  Zap,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
} from "lucide-react";
import { offeringsService } from "../../../services/api/settings";
import { OfferingPlan, OfferingAddon } from "../../../store/types/settings";

const ConsolePay: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [billingType, setBillingType] = useState<"Monthly" | "Quarterly" | "Yearly">(() => {
    const freq = searchParams.get("frequency");
    return freq === "YEARLY" ? "Yearly" : freq === "QUARTERLY" ? "Quarterly" : "Monthly";
  });
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<
    string[]
  >([]);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<
    string | null
  >(null);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [offeringPlans, setOfferingPlans] = useState<OfferingPlan[]>([]);
  const [offeringAddons, setOfferingAddons] = useState<OfferingAddon[]>([]);
  const [loading, setLoading] = useState(true);

  const organizationId = searchParams.get("Id");
  const consolePlanTier = searchParams.get("tier");
  const frequency = searchParams.get("frequency");
  const deploymentIds = searchParams.get("deploymentIds")?.split(",") || [];
  const integrationIds = searchParams.get("integrationIds")?.split(",") || [];

  useEffect(() => {
    const fetchOfferings = async () => {
      try {
        setLoading(true);
        const response = await offeringsService.getAll();
        if (response.status === "success" && response.data) {
          setOfferingPlans(response.data.CONSOLE.plans);
          setOfferingAddons(response.data.CONSOLE.addons);
        }
      } catch (error) {
        console.error("Error fetching console pay offerings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOfferings();
  }, []);

  const deploymentMainOptions = offeringAddons
    .filter((a) => a.category === "DEPLOYMENT_COST")
    .map((a) => ({
      id: a.id,
      name: a.name,
      price: typeof a.price === "string" ? parseFloat(a.price) : a.price,
    }));

  const deploymentFixedAddOns = offeringAddons
    .filter((a) => a.category === "DEPLOYMENT_ADDON")
    .map((a) => ({
      id: a.id,
      name: a.name,
      price: typeof a.price === "string" ? parseFloat(a.price) : a.price,
    }))
    .slice(0, 2);

  const plans = offeringPlans.map((plan) => ({
    id: plan.id,
    title: plan.title,
    monthlyPrice: parseFloat(plan.monthlyPrice),
    quarterlyPrice: plan.quarterlyPrice
      ? parseFloat(String(plan.quarterlyPrice))
      : parseFloat(plan.monthlyPrice) * 3,
    yearlyPrice: parseFloat(plan.yearlyPrice),
    users: plan.subtitle,
    features: plan.points,
    integrationServices:
      (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
        ? plan
            .costBreakdown!.deploymentCosts.filter((d: any) => d && d.name)
            .map((d: any) => ({
              name: d.name,
              cost: d.price.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            }))
        : plan.setupFee || plan.integrationCost
          ? [
              {
                name: "Standard Onboarding & Training",
                cost: (
                  parseFloat(plan.setupFee || plan.integrationCost || "0") || 0
                ).toLocaleString(),
              },
            ]
          : [],
  }));

  const integrations = offeringAddons
    .filter((a) => a.category === "INTEGRATION")
    .map((a) => ({
      id: a.id,
      name: a.name,
      price: typeof a.price === "string" ? parseFloat(a.price) : a.price,
    }));

  useEffect(() => {
    if (frequency === "YEARLY") setBillingType("Yearly");
    else if (frequency === "QUARTERLY") setBillingType("Quarterly");
    else setBillingType("Monthly");

    // Set deployment ID from URL
    const mainDep = deploymentMainOptions.find((d) =>
      deploymentIds.includes(d.id),
    );
    if (mainDep) setSelectedDeploymentId(mainDep.id);

    // Set integrations from URL
    if (integrationIds.length > 0) {
      setSelectedIntegrationIds(integrationIds.filter((i) => i));
    }
  }, [searchParams, frequency]);

  const toggleIntegration = (id: string) => {
    setSelectedIntegrationIds((prev) => {
      const newIds = prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id];

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("integrationIds", newIds.join(","));
        } else {
          p.delete("integrationIds");
        }
        return p;
      });
      return newIds;
    });
  };

  const removeIntegration = (id: string) => {
    setSelectedIntegrationIds((prev) => {
      const newIds = prev.filter((i) => i !== id);
      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("integrationIds", newIds.join(","));
        } else {
          p.delete("integrationIds");
        }
        return p;
      });
      return newIds;
    });
  };

  const currentPlan = plans.find((p) => p.title === consolePlanTier);
  const planFeatures = currentPlan
    ? currentPlan.features.map((f) => ({
        id: f,
        displayName: f,
        price: 0,
      }))
    : [];

  const selectedDeploymentOption = deploymentMainOptions.find(
    (d) => d.id === selectedDeploymentId,
  );

  const selectedIntegrations = integrations.filter((i) =>
    selectedIntegrationIds.includes(i.id),
  );

  const allSelectedItems: SelectedItem[] = [
    // ...planFeatures,
    ...(selectedDeploymentOption
      ? [
          {
            id: selectedDeploymentOption.id,
            displayName: selectedDeploymentOption.name,
            price: selectedDeploymentOption.price,
          },
          ...deploymentFixedAddOns.map((d) => ({
            id: d.id,
            displayName: d.name,
            price: d.price,
          })),
        ]
      : []),
    ...selectedIntegrations.map((i) => ({
      id: i.id,
      displayName: i.name,
      price: i.price,
    })),
  ];

  const calculateFloatingTotal = () => {
    const total = allSelectedItems.reduce(
      (sum, item) => sum + (item.price || 0),
      0,
    );
    return total.toLocaleString();
  };

  const isYearly = billingType === "Yearly";

  const handleContinue = () => {
    navigate(`/order-form?${searchParams.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-abel pb-20">
      <Navbar title="Login" link="/login" />
      <div className="max-w-7xl mx-auto px-4 pt-10">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div
              onClick={() => history.back()}
              className="text-blue-600 hover:text-blue-800 cursor-pointer"
            >
              <ArrowLeft className="w-6 h-6" />
            </div>
            <h1 className="text-3xl font-medium text-gray-900">
              Review Selection
            </h1>
          </div>
          <button
            onClick={() => setShowAllPlans(!showAllPlans)}
            className="px-6 py-2 bg-white border border-blue-600 text-blue-600 rounded-full hover:bg-blue-50 transition-colors font-medium flex items-center gap-2"
          >
            <Zap className="w-4 h-4" />
            {showAllPlans ? "Hide other plans" : "Compare all plans"}
          </button>
        </div>

        <PricingToggle
          billingType={billingType}
          setBillingType={(type) => {
            setBillingType(type);
          setSearchParams((p) => {
            p.set("frequency", type === "Yearly" ? "YEARLY" : type === "Quarterly" ? "QUARTERLY" : "MONTHLY");
            return p;
          });
          }}
        />

        {showAllPlans && (
          <div className="mt-10 mb-16 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan, index) => {
                const isSelected = plan.title === consolePlanTier;
                const isExpanded = expandedPlan === index;
                return (
                  <div
                    key={index}
                    className={`rounded-[20px] shadow-lg overflow-hidden flex flex-col bg-white border-2 transition-all duration-300 ${
                      isSelected
                        ? "border-blue-600 ring-4 ring-blue-50 scale-[1.02]"
                        : "border-transparent hover:border-gray-200"
                    }`}
                  >
                    <div className="p-8 flex flex-col h-full">
                      <div className="flex justify-between items-start mb-1">
                        <h3 className="text-2xl font-medium text-gray-900">
                          {plan.title}
                        </h3>
                        {isSelected && (
                          <span className="bg-blue-600 text-white text-[10px] uppercase px-2 py-0.5 rounded-full font-bold">
                            Selected
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-blue-600 font-medium mb-4">
                        {plan.users}
                      </p>

                      <div className="mb-6">
                        <div className="flex items-baseline">
                          <span className="text-3xl font-normal text-gray-900">
                            <span className="text-lg align-super">N</span>
                            {(billingType === "Yearly"
                              ? plan.yearlyPrice
                              : billingType === "Quarterly"
                                ? plan.quarterlyPrice
                                : plan.monthlyPrice
                            ).toLocaleString()}
                          </span>
                          <span className="text-gray-500 ml-1 text-sm">
                            /{billingType === "Yearly" ? "year" : billingType === "Quarterly" ? "quarter" : "month"}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-8 flex-grow">
                        {plan.features.length > 5 && (
                          <button
                            onClick={() =>
                              setExpandedPlan(isExpanded ? null : index)
                            }
                            className="text-blue-600 text-xs font-semibold flex items-center gap-1 mb-6 hover:underline"
                          >
                            {isExpanded ? (
                              <>
                                Show less <ChevronUp className="w-3 h-3" />
                              </>
                            ) : (
                              <>
                                Show all features{" "}
                                <ChevronDown className="w-3 h-3" />
                              </>
                            )}
                          </button>
                        )}

                        {(plan as any).integrationServices?.some(
                          (s: any) => s.name,
                        ) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedPlan(isExpanded ? null : index);
                            }}
                            className={`w-full py-2.5 px-3 mb-6 border rounded-lg font-m hover:transition-all flex items-center justify-center gap-2 border-gray-100 text-gray-500 hover:bg-gray-50`}
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp className="w-4 h-4" />
                                <span className="text-xs">
                                  Hide Integration Plan
                                </span>
                              </>
                            ) : (
                              <>
                                <ChevronUp className="w-4 h-4 rotate-180" />
                                <span className="text-xs">
                                  View Integration Plan
                                </span>
                              </>
                            )}
                          </button>
                        )}

                        {isExpanded &&
                          (plan as any).integrationServices?.some(
                            (s: any) => s.name,
                          ) && (
                            <div
                              className={`mb-6 pb-6 border-t pt-6 border-gray-100`}
                            >
                              <div className="flex justify-between items-center mb-4">
                                <h4
                                  className={`text-sm font-bold text-gray-900`}
                                >
                                  Deployment & Setup Plan
                                </h4>
                                <span
                                  className={`text-[10px] font-bold text-gray-400 uppercase tracking-wider`}
                                >
                                  NGN
                                </span>
                              </div>
                              <ul className="space-y-3">
                                {(plan as any).integrationServices
                                  .filter((s: any) => s.name)
                                  .map((service: any, i: number) => (
                                    <li
                                      key={i}
                                      className="flex items-start justify-between text-xs"
                                    >
                                      <span className="text-gray-500 leading-relaxed pr-4">
                                        {service.name}
                                      </span>
                                      <span className="font-bold text-gray-900 whitespace-nowrap">
                                        {service.cost}
                                      </span>
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          )}
                      </div>

                      {!isSelected ? (
                        <button
                          onClick={() => {
                            setSearchParams((p) => {
                              p.set("tier", plan.title);
                              return p;
                            });
                          }}
                          className="w-full py-3 px-4 rounded-xl border-2 border-blue-600 text-blue-600 font-bold hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center gap-2"
                        >
                          Switch to this plan
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <div className="w-full py-3 px-4 rounded-xl bg-blue-50 text-blue-600 font-bold text-center border border-blue-200">
                          Current Selection
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8 mt-10">
          <div className="lg:col-span-1">
            {currentPlan && (
              <div className="bg-white rounded-[20px] shadow-xl p-8 border border-blue-100 ring-2 ring-blue-600 relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Selected Plan
                </div>
                <h3 className="text-2xl font-medium text-gray-900 mb-2">
                  {currentPlan.title}
                </h3>
                <p className="text-blue-600 text-sm font-medium mb-6">
                  {currentPlan.users}
                </p>
                <div className="mb-8">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-normal text-gray-900">
                      <span className="text-lg align-super">N</span>
                      {(billingType === "Yearly"
                        ? currentPlan.yearlyPrice
                        : billingType === "Quarterly"
                          ? currentPlan.quarterlyPrice
                          : currentPlan.monthlyPrice
                      ).toLocaleString()}
                    </span>
                    <span className="text-gray-500 ml-1 text-sm">
                      /{billingType === "Yearly" ? "year" : billingType === "Quarterly" ? "quarter" : "month"}
                    </span>
                  </div>
                </div>
                <div className="space-y-4">
                  {currentPlan.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </div>
                  ))}
                </div>

                {currentPlan.integrationServices &&
                  currentPlan.integrationServices.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-gray-100">
                      <button
                        onClick={() =>
                          setExpandedPlan(expandedPlan === -1 ? null : -1)
                        }
                        className="w-full flex items-center justify-between text-left mb-4 group"
                      >
                        <span className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                          Deployment & Integration Plan
                        </span>
                        {expandedPlan === -1 ? (
                          <ChevronUp className="w-4 h-4 text-blue-600" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                        )}
                      </button>

                      {expandedPlan === -1 && (
                        <div className="space-y-4">
                          <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                            <span>Integration Service</span>
                            <span>Cost (NGN)</span>
                          </div>
                          <ul className="space-y-3">
                            {currentPlan.integrationServices.map(
                              (service, i) => (
                                <li
                                  key={i}
                                  className="flex items-start justify-between text-xs"
                                >
                                  <span className="text-gray-500 leading-relaxed pr-4">
                                    {service.name}
                                  </span>
                                  <span className="font-bold text-gray-900 whitespace-nowrap">
                                    {service.cost}
                                  </span>
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                <button
                  onClick={handleContinue}
                  className="w-full mt-10 py-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition shadow-lg hover:shadow-blue-200"
                >
                  Continue to Order Form
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <AddOnsConsole
              organizationId={organizationId || undefined}
              selectedIds={selectedIntegrationIds}
              onToggle={toggleIntegration}
            />
          </div>
        </div>
      </div>

      <SelectedFeaturesFloating
        selectedItems={allSelectedItems}
        onRemove={(id) => {
          if (id === selectedDeploymentId) {
            // Can't remove deployment from here yet, maybe just don't allow it
          } else {
            removeIntegration(id);
          }
        }}
        totalPrice={calculateFloatingTotal()}
        billingType={billingType}
        title="Console Selection"
      />
    </div>
  );
};

export default ConsolePay;
